import { useState, useEffect, useRef } from 'react';
import { themeI18n } from '../utils/i18n_helper';
import { ConvertToShopifyProduct } from '../utils/ConvertToShopifyProduct';
import { ProductCard } from './ProductCard';
import { ButtonContent } from './ButtonContent';
import { FiltersDrawer } from './FiltersDrawer';
import { PromoTile } from './PromoTile';

const listingData = JSON.parse(document.querySelector('[data-product-listing-data]').innerHTML);
const url = new URL(window.location.href);

const narrowByInitial = [];
url.searchParams.forEach((value, key) => {
  if (key.startsWith('filter.')) {
    const paramName = key.substring('filter.'.length);
    narrowByInitial.push([paramName, value]);
  }
});

const validSortOptions = [
  "reviews",
  "relevancy",
  "a_to_z",
  "z_to_a",
  "price_min_to_max",
  "price_max_to_min",
  "creation_date_oldest",
  "creation_date",
  "popularity",
];

export const ProductListing = () => {
  const [products, setProducts] = useState([]);
  const [results, setResults] = useState({});
  const [sortBy, setSortBy] = useState(url.searchParams.get("sort_by") || 'relevancy');
  const [narrowBy, setNarrowBy] = useState(narrowByInitial);
  const [facets, setFacets] = useState([]);
  const [page, setPage] = useState(Number(url.searchParams.get("page")) || 1);
  const [prevPage, setPrevPage] = useState((Number(url.searchParams.get("page")) || 1) - 1);
  const [nextPage, setNextPage] = useState((Number(url.searchParams.get("page")) || 1) + 1);
  const [loading, setLoading] = useState(false);

  const listingRef = useRef(null);

  useEffect(() => {
    fetchSmartProductListing(page, "replace");
  }, [sortBy, narrowBy]);

  const fetchSmartProductListing = async (page, action) => {
    const sdkFunction = listingData.collectionId ? "smartCollections" : "fullTextSearch";
    let responseReceived = false; // Fast Simon sometimes sends the response in two batches, one with only products and one with facets and products, this flag is to avoid setting the products twice
    setLoading(true);

    try {
      window.FastSimonSDK[sdkFunction]({
        term: listingData.collectionId ? null : listingData.search_term,
        categoryID: listingData.collectionId ? listingData.collectionId : null,
        narrowBy: narrowBy,
        sortBy: sortBy,
        page: page,
        productsPerPage: listingData.productsPerPage,
        withAttributes: true,
        callback: (response) => {
          console.log('response', response); // gonna keep this for debugging purposes for now

          setResults(response.payload);

          if (response.payload.isNoResults) {
            setProducts([]);

            if (listingRef.current.closest('#Main_content').querySelector('.shopify-section.search-main + .shopify-section.collection-list')) {
              listingRef.current.closest('#Main_content').querySelector('.shopify-section.search-main + .shopify-section.collection-list').style.display = 'block';
            }

            return;
          } else {
            // if there are results, hide the "You may also like" section on search page
            if (listingRef.current.closest('#Main_content').querySelector('.shopify-section.search-main + .shopify-section.collection-list')) {
              listingRef.current.closest('#Main_content').querySelector('.shopify-section.search-main + .shopify-section.collection-list').style.display = 'none';
            }
          }

          if(!responseReceived) {
            if (action === "prepend") {
              setProducts((prevProducts) => [...response.payload.products, ...prevProducts]);
            } else if (action == "replace") {
              setProducts(response.payload.products);
            } else {
              setProducts((prevProducts) => [...prevProducts, ...response.payload.products]);
            }
          }

          if (response.action == 'facets and products') {
            setFacets(response.payload.facets);
          }
          responseReceived = true;
        }
      });

      if (listingData.collectionId) {
        // Collection Viewed event
        window.FastSimonSDK.event({
          eventName: window.FastSimonEventName.SmartCollectionPreformed,
          data: {
            categoryID: listingData.collectionId, // (Required)
            narrowBy: narrowBy,
            sortBy: sortBy,
            products: products && products.map(({id}) => id).toString(),
            rescount: results && results.totalResults,
            pagenum: page
          }
        });
      } else if (listingData.search_term) {
        // Search Viewed event
        window.FastSimonSDK.event({
          eventName: window.FastSimonEventName.SearchPerformed,
          data: {
            query: listingData.search_term || "",
            narrowBy: narrowBy,
            sortBy: sortBy
          }
        });
      }
    } catch (error) {
      throw new Error("Error fetching smart collection: " + error.message);
    }
    setLoading(false);
  }

  const loadPrevious = (event, prevPage) => {
    event.preventDefault();

    // update URL
    url.searchParams.set('page', prevPage);
    window.history.pushState(null, null, url.toString());

    fetchSmartProductListing(prevPage, "prepend");

    setPage(prevPage);
    setPrevPage(prevPage - 1);
  }

  const loadMore = (event, nextPage) => {
    event.preventDefault();

    // update URL
    url.searchParams.set('page', nextPage);
    window.history.pushState(null, null, url.toString());

    fetchSmartProductListing(nextPage, "append");

    setPage(nextPage);
    setNextPage(nextPage + 1);
  }

  return (
    <div className='product-listing__main load-more-pagination' ref={listingRef}>
      <div className='product-listing__top-bar'>
        {(listingData.enableSort || listingData.enableFilters) && (
          <FiltersDrawer
            facets={facets}
            sortBy={sortBy}
            setSortBy={setSortBy}
            narrowBy={narrowBy}
            setNarrowBy={setNarrowBy}
            setPage={setPage}
            setPrevPage={setPrevPage}
            setNextPage={setNextPage}
          />
        )}

        <div className='product-listing__sort-by-quantity-wrapper '>
          {listingData.enableSort && (
            <div className='product-listing__sort-by-desktop hide-desktop-down'>
              <label htmlFor='desktop-sort-by'>
                {themeI18n('collection.sort_by_label')}:
              </label>

              <select
                id='desktop-sort-by'
                value={sortBy}
                onChange={(event)=> {
                  url.searchParams.set('sort_by', event.target.value);
                  url.searchParams.set('page', 1);
                  window.history.pushState(null, null, url.toString());

                  setPage(1);
                  setSortBy(event.target.value);
                  setPrevPage(0);
                  setNextPage(2);
                }}
              >
                {validSortOptions.map((option) => {
                  return (
                    <option key={option} value={option}>
                      {themeI18n(`shopify.collections.sorting_fs.${option}`)}
                    </option>
                  )
                })}
              </select>
            </div>
          )}

          <span className='product-listing__quantity'>{products.length} {themeI18n("collection.items")}</span>
        </div>
      </div>

      {prevPage > 0 && (
        <a className='button' href={listingData.collectionId ?  `/collections/${listingData.collectionHandle}?page=${prevPage}` : `/search?q=${listingData.search_term}&page=${prevPage}`} onClick={(event)=> loadPrevious(event, prevPage)}>
                    <ButtonContent text={themeI18n('general.pagination.load_previous')} />
        </a>
      )}

      {results.isNoResults && (
        <p>{themeI18n('search.no_results_match_filters')}</p>
      )}

      <div className={loading ? "product-listing__item-grid loading": "product-listing__item-grid"}>
        {products.map((product, index) => {
          if (!product.promotile) {
            const shopifyProduct = ConvertToShopifyProduct(product);

            return (
              <ProductCard
                product={shopifyProduct}
                key={product.id}
                disableQuickView={listingData.disableQuickView}
                withinCollection={listingData.collectionId ? listingData.collectionHandle : false}
                collectionId={listingData.collectionId}
                searchTerm={listingData.search_term}
                position={index + 1}
              />
            )
          } else {
            return (
              <PromoTile key={product.id} promoTile={product} />
            )
          }
        })}
      </div>

      <div className='product-listing__load-more-wrapper'>
        {nextPage <= results.pageCount && (
          <a className='button' href={listingData.collectionId ? `/collections/${listingData.collectionHandle}?page=${nextPage}` : `/search?q=${listingData.search_term}&page=${nextPage}`} onClick={(event)=> loadMore(event, nextPage)}>
            <ButtonContent text={themeI18n('general.pagination.load_more')} />
          </a>
        )}

        <p>{products.length} {themeI18n('general.pagination.current_preposition')} {results.totalResults}</p>
      </div>
    </div>
  )
}
